<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'synced']);
// Form state
const { t } = useI18n();
const name = ref('');
const category = ref('UTILITY');
const language = ref('en_US');

const headerType = ref('NONE'); // NONE | TEXT | IMAGE | DOCUMENT | VIDEO
const headerText = ref('');
const headerMediaFile = ref(null);
const headerMediaPreviewUrl = ref('');
const headerFileInput = ref(null);
const sampleVariables = ref({
  1: 'John',
  2: 'inquiry',
  3: 'support',
  4: 'status',
  5: 'issue',
});
const editingVariable = ref(null);
const editingValue = ref('');
const isSubmitting = ref(false);

// Variable type definitions
const variableTypes = ref({
  1: 'text',
  2: 'text',
  3: 'text',
  4: 'text',
  5: 'text',
});

// WhatsApp-supported template languages (codes only to avoid i18n label churn)
const supportedTemplateLanguages = [
  'af',
  'sq',
  'ar',
  'ar_EG',
  'ar_AE',
  'ar_LB',
  'ar_MA',
  'ar_QA',
  'az',
  'be_BY',
  'bn',
  'bn_IN',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'prs_AF',
  'nl',
  'nl_BE',
  'en',
  'en_GB',
  'en_US',
  'en_AE',
  'en_AU',
  'en_CA',
  'en_GH',
  'en_IE',
  'en_IN',
  'en_JM',
  'en_MY',
  'en_NZ',
  'en_QA',
  'en_SG',
  'en_UG',
  'en_ZA',
  'et',
  'fil',
  'fi',
  'fr',
  'fr_BE',
  'fr_CA',
  'fr_CH',
  'fr_CI',
  'fr_MA',
  'ka',
  'de',
  'de_AT',
  'de_CH',
  'el',
  'gu',
  'ha',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'rw_RW',
  'ko',
  'ky_KG',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mr',
  'nb',
  'ps_AF',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'si_LK',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_CL',
  'es_CO',
  'es_CR',
  'es_DO',
  'es_EC',
  'es_HN',
  'es_MX',
  'es_PA',
  'es_PE',
  'es_ES',
  'es_UY',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zu',
];

const languageLabels = {
  af: 'Afrikaans',
  sq: 'Albanian',
  ar: 'Arabic',
  ar_EG: 'Arabic (EGY)',
  ar_AE: 'Arabic (UAE)',
  ar_LB: 'Arabic (LBN)',
  ar_MA: 'Arabic (MAR)',
  ar_QA: 'Arabic (QAT)',
  az: 'Azerbaijani',
  be_BY: 'Belarusian',
  bn: 'Bengali',
  bn_IN: 'Bengali (IND)',
  bg: 'Bulgarian',
  ca: 'Catalan',
  zh_CN: 'Chinese (CHN)',
  zh_HK: 'Chinese (HKG)',
  zh_TW: 'Chinese (TAI)',
  hr: 'Croatian',
  cs: 'Czech',
  da: 'Danish',
  prs_AF: 'Dari',
  nl: 'Dutch',
  nl_BE: 'Dutch (BEL)',
  en: 'English',
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  en_AE: 'English (UAE)',
  en_AU: 'English (AUS)',
  en_CA: 'English (CAN)',
  en_GH: 'English (GHA)',
  en_IE: 'English (IRL)',
  en_IN: 'English (IND)',
  en_JM: 'English (JAM)',
  en_MY: 'English (MYS)',
  en_NZ: 'English (NZL)',
  en_QA: 'English (QAT)',
  en_SG: 'English (SGP)',
  en_UG: 'English (UGA)',
  en_ZA: 'English (ZAF)',
  et: 'Estonian',
  fil: 'Filipino',
  fi: 'Finnish',
  fr: 'French',
  fr_BE: 'French (BEL)',
  fr_CA: 'French (CAN)',
  fr_CH: 'French (CHE)',
  fr_CI: 'French (CIV)',
  fr_MA: 'French (MAR)',
  ka: 'Georgian',
  de: 'German',
  de_AT: 'German (AUT)',
  de_CH: 'German (CHE)',
  el: 'Greek',
  gu: 'Gujarati',
  ha: 'Hausa',
  he: 'Hebrew',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  ga: 'Irish',
  it: 'Italian',
  ja: 'Japanese',
  kn: 'Kannada',
  kk: 'Kazakh',
  rw_RW: 'Kinyarwanda',
  ko: 'Korean',
  ky_KG: 'Kyrgyz (Kyrgyzstan)',
  lo: 'Lao',
  lv: 'Latvian',
  lt: 'Lithuanian',
  mk: 'Macedonian',
  ms: 'Malay',
  ml: 'Malayalam',
  mr: 'Marathi',
  nb: 'Norwegian',
  ps_AF: 'Pashto',
  fa: 'Persian',
  pl: 'Polish',
  pt_BR: 'Portuguese (BR)',
  pt_PT: 'Portuguese (POR)',
  pa: 'Punjabi',
  ro: 'Romanian',
  ru: 'Russian',
  sr: 'Serbian',
  si_LK: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  es: 'Spanish',
  es_AR: 'Spanish (ARG)',
  es_CL: 'Spanish (CHL)',
  es_CO: 'Spanish (COL)',
  es_CR: 'Spanish (CRI)',
  es_DO: 'Spanish (DOM)',
  es_EC: 'Spanish (ECU)',
  es_HN: 'Spanish (HND)',
  es_MX: 'Spanish (MEX)',
  es_PA: 'Spanish (PAN)',
  es_PE: 'Spanish (PER)',
  es_ES: 'Spanish (SPA)',
  es_UY: 'Spanish (URY)',
  sw: 'Swahili',
  sv: 'Swedish',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  zu: 'Zulu',
};

const bodyText = ref('Hello! This is a test template message.');
const footerText = ref('');

// Buttons
const quickReplies = ref([]); // up to 3 labels
const ctaButtons = ref([]); // up to 2 { type: 'URL'|'PHONE_NUMBER', label, value }

// Derived
const variablesInBody = computed(() => {
  const matches = bodyText.value.match(/\{{2}\d+\}{2}/g) || [];
  return Array.from(new Set(matches));
});

const variablesInHeader = computed(() => {
  const matches = headerText.value.match(/\{{2}\d+\}{2}/g) || [];
  return Array.from(new Set(matches));
});

const canAddQuickReply = computed(() => quickReplies.value.length < 3);
const canAddCta = computed(() => ctaButtons.value.length < 2);

const headerHasText = computed(() => headerType.value === 'TEXT');
const headerHasMedia = computed(() =>
  ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(headerType.value)
);
const headerFileName = computed(() => headerMediaFile.value?.name || '');
const headerAcceptAttr = computed(() => {
  if (headerType.value === 'IMAGE') return 'image/*';
  if (headerType.value === 'VIDEO') return 'video/*';
  if (headerType.value === 'DOCUMENT') return 'application/*,application/pdf';
  return '*/*';
});

// Helpers
const insertVariableAtCursor = () => {
  const nextIndex = variablesInBody.value.length + 1;
  const token = `{{${nextIndex}}}`;
  // naive append if we don't have cursor APIs; acceptable for MVP
  bodyText.value = `${bodyText.value}${bodyText.value ? ' ' : ''}${token}`;
};

const insertHeaderVariable = () => {
  const nextIndex = variablesInHeader.value.length + 1;
  const token = `{{${nextIndex}}}`;
  headerText.value = `${headerText.value}${headerText.value ? ' ' : ''}${token}`;
};

const addQuickReply = () => {
  if (!canAddQuickReply.value) return;
  quickReplies.value.push('');
};

const removeQuickReply = index => {
  quickReplies.value.splice(index, 1);
};

const addCta = () => {
  if (!canAddCta.value) return;
  ctaButtons.value.push({ type: 'URL', label: '', value: '' });
};

const removeCta = index => {
  ctaButtons.value.splice(index, 1);
};

// Preview helpers
const replaceVariables = text => {
  if (!text) return '';
  return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
    return (
      sampleVariables.value[num] ||
      t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLE_PLACEHOLDER', { num })
    );
  });
};

const getPreviewHeader = computed(() => {
  if (headerType.value === 'TEXT') {
    return (
      replaceVariables(headerText.value) ||
      t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_PREVIEW_PH')
    );
  }
  if (headerHasMedia.value) {
    if (headerType.value === 'IMAGE') {
      return headerMediaPreviewUrl.value
        ? t('INBOX_MGMT.TEMPLATES.CREATE.IMAGE_PREVIEW')
        : t('INBOX_MGMT.TEMPLATES.CREATE.IMAGE_UPLOAD');
    }
    if (headerType.value === 'VIDEO') {
      return headerMediaPreviewUrl.value
        ? t('INBOX_MGMT.TEMPLATES.CREATE.VIDEO_PREVIEW')
        : t('INBOX_MGMT.TEMPLATES.CREATE.VIDEO_UPLOAD');
    }
    if (headerType.value === 'DOCUMENT') {
      return headerMediaFile.value
        ? t('INBOX_MGMT.TEMPLATES.CREATE.DOCUMENT_PREVIEW', {
            name: headerFileName.value,
          })
        : t('INBOX_MGMT.TEMPLATES.CREATE.DOCUMENT_UPLOAD');
    }
  }
  return '';
});

const getPreviewBody = computed(() => {
  return (
    replaceVariables(bodyText.value) ||
    t('INBOX_MGMT.TEMPLATES.CREATE.BODY_PREVIEW_PH')
  );
});

const getPreviewFooter = computed(() => {
  return replaceVariables(footerText.value) || '';
});

const getPreviewQuickReplies = computed(() => {
  return quickReplies.value.map(
    qr => qr || t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLY_PH')
  );
});

const getPreviewCtaButtons = computed(() => {
  return ctaButtons.value.map(btn => ({
    type: btn.type,
    label:
      btn.label ||
      (btn.type === 'URL'
        ? t('INBOX_MGMT.TEMPLATES.CREATE.OPEN_URL')
        : t('INBOX_MGMT.TEMPLATES.CREATE.CALL_PHONE')),
    value:
      btn.value || (btn.type === 'URL' ? 'https://example.com' : '+1234567890'),
  }));
});

const resetForm = () => {
  name.value = '';
  category.value = 'UTILITY';
  language.value = 'en';
  headerType.value = 'NONE';
  headerText.value = '';
  if (headerMediaPreviewUrl.value)
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  headerMediaFile.value = null;
  headerMediaPreviewUrl.value = '';
  bodyText.value = '';
  footerText.value = '';
  quickReplies.value = [];
  ctaButtons.value = [];
  // Reset sample variables to defaults
  sampleVariables.value = {
    1: 'John',
    2: 'inquiry',
    3: 'support',
    4: 'status',
    5: 'issue',
  };
};

const onHeaderFileChange = event => {
  const file = event.target?.files?.[0];
  if (headerMediaPreviewUrl.value)
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  headerMediaFile.value = file || null;
  headerMediaPreviewUrl.value = file ? URL.createObjectURL(file) : '';
};

const clearHeaderFile = () => {
  if (headerMediaPreviewUrl.value)
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  headerMediaFile.value = null;
  headerMediaPreviewUrl.value = '';
  if (headerFileInput.value) headerFileInput.value.value = '';
};

const buildPayload = async () => {
  // Convert file to base64 if present
  let mediaContent = null;
  if (headerMediaFile.value) {
    mediaContent = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.readAsDataURL(headerMediaFile.value);
    });
  }

  return {
    name: name.value,
    category: category.value,
    language: language.value,
    components: [
      ...(headerType.value !== 'NONE'
        ? [
            {
              type: 'HEADER',
              format: headerType.value,
              text: headerType.value === 'TEXT' ? headerText.value : undefined,
              example:
                headerType.value === 'TEXT' && headerText.value?.includes('{{')
                  ? 'Example Header'
                  : undefined,
              // Include media file data for upload
              media: headerMediaFile.value
                ? {
                    name: headerMediaFile.value.name,
                    size: headerMediaFile.value.size,
                    type: headerMediaFile.value.type,
                    content: mediaContent,
                  }
                : undefined,
            },
          ]
        : []),
      {
        type: 'BODY',
        text: bodyText.value,
        examples: bodyText.value?.includes('{{')
          ? sampleVariables.value
          : undefined,
      },
      ...(footerText.value ? [{ type: 'FOOTER', text: footerText.value }] : []),
      ...(quickReplies.value.length || ctaButtons.value.length
        ? [
            {
              type: 'BUTTONS',
              buttons: [
                ...quickReplies.value.map(label => ({
                  type: 'QUICK_REPLY',
                  text: label,
                })),
                ...ctaButtons.value
                  .map(btn => {
                    if (btn.type === 'URL') {
                      return {
                        type: 'URL',
                        text: btn.label,
                        url: btn.value,
                        example: btn.value?.includes('{{')
                          ? 'example'
                          : undefined,
                      };
                    }
                    if (btn.type === 'PHONE_NUMBER') {
                      return {
                        type: 'PHONE_NUMBER',
                        text: btn.label,
                        phone_number: btn.value,
                      };
                    }
                    return null;
                  })
                  .filter(Boolean),
              ],
            },
          ]
        : []),
    ],
  };
};

const onCreate = async () => {
  if (isSubmitting.value) return;
  if (!name.value || !category.value || !language.value || !bodyText.value) {
    useAlert(t('INBOX_MGMT.TEMPLATES.CREATE.VALIDATION_REQUIRED'));
    return;
  }

  // Validate template name format (alphanumeric and underscores only)
  if (!/^[a-zA-Z0-9_]+$/.test(name.value)) {
    useAlert(t('INBOX_MGMT.TEMPLATES.CREATE.NAME_VALIDATION_ERROR'));
    return;
  }

  // Validate template body for problematic patterns
  if (bodyText.value.includes('{{') && bodyText.value.includes('}}')) {
    // Check for non-sequential or problematic placeholder patterns
    const matches = bodyText.value.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map(match =>
      parseInt(match.replace(/\{\{|\}\}/g, ''))
    );

    // Check if numbers are sequential starting from 1
    const expectedNumbers = Array.from(
      { length: numbers.length },
      (_, i) => i + 1
    );
    if (!arraysEqual(numbers.sort(), expectedNumbers)) {
      useAlert(t('INBOX_MGMT.TEMPLATES.CREATE.PLACEHOLDER_ERROR'));
      return;
    }

    // Check for maximum number of variables (WhatsApp limit is typically 10)
    if (numbers.length > 10) {
      useAlert(t('INBOX_MGMT.TEMPLATES.CREATE.MAX_VARIABLES_ERROR'));
      return;
    }
  }

  try {
    isSubmitting.value = true;
    const payload = await buildPayload();

    // Get account and inbox IDs from the current route
    const accountId = window.location.pathname.split('/')[3]; // accounts/:accountId/...
    const inboxId = window.location.pathname.split('/')[6]; // settings/inboxes/:inboxId/...

    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/whatsapp_templates`,
      {
        template: payload,
        inbox_id: inboxId,
      }
    );

    if (response.status === 201) {
      useAlert(t('INBOX_MGMT.TEMPLATES.CREATE.SUCCESS_MESSAGE'));
      // Trigger sync so latest template state is available
      try {
        const syncUrl = `/api/v1/accounts/${accountId}/whatsapp_templates?inbox_id=${inboxId}`;
        const syncResp = await window.axios.get(syncUrl);
        emit('synced', syncResp.data?.templates || []);
      } catch (_) {
        // Ignore sync failure for UX; list can be refreshed manually
      }
      // Close modal (if wrapped in a modal) and reset form
      emit('close');
      resetForm();
    } else {
      const errorMessage =
        response.data.error ||
        response.data.details ||
        t('INBOX_MGMT.TEMPLATES.CREATE.ERROR_MESSAGE');
      useAlert(errorMessage);
    }
  } catch (error) {
    // Handle WhatsApp API errors gracefully without showing console errors
    const errorData = error.response?.data;
    let errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.ERROR_MESSAGE');

    if (errorData?.error) {
      // Handle specific WhatsApp API errors
      if (errorData.error.includes('Not allowed nest placeholder')) {
        errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.PLACEHOLDER_ERROR');
      } else if (errorData.error.includes('App ID not found')) {
        errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.APP_ID_ERROR');
      } else {
        errorMessage = errorData.error;
      }
    } else if (errorData?.details) {
      errorMessage = errorData.details;
    }

    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// Variable editing helpers
const startEditingVariable = varNum => {
  editingVariable.value = varNum;
  editingValue.value = sampleVariables.value[varNum] || '';
};

const saveVariableEdit = () => {
  if (editingVariable.value) {
    sampleVariables.value[editingVariable.value] = editingValue.value;
    editingVariable.value = null;
    editingValue.value = '';
  }
};

const cancelVariableEdit = () => {
  editingVariable.value = null;
  editingValue.value = '';
};

// Get all variables used in the template
const getAllVariables = computed(() => {
  const variableSet = new Set();

  // Check header text
  if (headerType.value === 'TEXT' && headerText.value) {
    const matches = headerText.value.match(/\{\{(\d+)\}\}/g) || [];
    matches.forEach(match => {
      const num = match.replace(/\{\{|\}\}/g, '');
      variableSet.add(parseInt(num, 10));
    });
  }

  // Check body text
  if (bodyText.value) {
    const matches = bodyText.value.match(/\{\{(\d+)\}\}/g) || [];
    matches.forEach(match => {
      const num = match.replace(/\{\{|\}\}/g, '');
      variableSet.add(parseInt(num, 10));
    });
  }

  // Check footer text
  if (footerText.value) {
    const matches = footerText.value.match(/\{\{(\d+)\}\}/g) || [];
    matches.forEach(match => {
      const num = match.replace(/\{\{|\}\}/g, '');
      variableSet.add(parseInt(num, 10));
    });
  }

  return Array.from(variableSet).sort((a, b) => a - b);
});

const formatVariable = varNum => `{{${varNum}}}`;

// Helper function to compare arrays
const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};
</script>

<template>
  <div
    class="bg-n-background outline outline-1 outline-n-container rounded-lg p-4 mb-6 max-h-[85vh] overflow-auto"
  >
    <div class="mb-4">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.SUBTITLE') }}
      </p>
    </div>

    <div class="grid grid-cols-[20%_40%_20%] gap-4">
      <!-- Left: 20% Name / Category / Language -->
      <div class="space-y-4">
        <div>
          <label class="block text-xs text-n-slate-11 mb-1">{{
            t('INBOX_MGMT.TEMPLATES.CREATE.NAME')
          }}</label>
          <input
            v-model="name"
            type="text"
            pattern="[a-zA-Z0-9_]+"
            class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
            :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.NAME_PH')"
            title="Only letters, numbers, and underscores allowed"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.NAME_HELP') }}
          </p>
        </div>

        <div>
          <label class="block text-xs text-n-slate-11 mb-1">{{
            t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORY')
          }}</label>
          <select
            v-model="category"
            class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
          >
            <option value="UTILITY">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.UTILITY') }}
            </option>
            <option value="MARKETING">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.MARKETING') }}
            </option>
            <option value="AUTHENTICATION">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.AUTHENTICATION') }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-xs text-n-slate-11 mb-1">{{
            t('INBOX_MGMT.TEMPLATES.CREATE.LANGUAGE')
          }}</label>
          <select
            v-model="language"
            class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
          >
            <option
              v-for="code in supportedTemplateLanguages"
              :key="code"
              :value="code"
            >
              {{ `${languageLabels[code] || code} (${code})` }}
            </option>
          </select>
        </div>
      </div>

      <!-- Center: 40% Header / Body / Footer / Buttons -->
      <div class="space-y-4">
        <!-- Header -->
        <div class="outline outline-1 outline-n-weak rounded-lg p-3">
          <div class="flex items-center gap-3">
            <div class="flex-1">
              <label class="block text-xs text-n-slate-11 mb-1">{{
                t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPE')
              }}</label>
              <select
                v-model="headerType"
                class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
              >
                <option value="NONE">
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.NONE') }}
                </option>
                <option value="TEXT">
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.TEXT') }}
                </option>
                <option value="IMAGE">
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.IMAGE') }}
                </option>
                <option value="DOCUMENT">
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.DOCUMENT') }}
                </option>
                <option value="VIDEO">
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.VIDEO') }}
                </option>
              </select>
            </div>
            <div v-if="headerHasText" class="flex-[2]">
              <label class="block text-xs text-n-slate-11 mb-1">{{
                t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TEXT')
              }}</label>
              <input
                v-model="headerText"
                type="text"
                class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
                :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TEXT_PH')"
              />
              <div class="mt-2 flex items-center justify-between">
                <span class="text-[11px] text-n-slate-11">{{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLES') }}:
                  {{ variablesInHeader.length }}</span>
                <button
                  class="h-8 px-2 rounded-md text-xs bg-n-brand text-white hover:bg-n-brand-dark"
                  type="button"
                  @click="insertHeaderVariable"
                >
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.INSERT_VARIABLE') }}
                </button>
              </div>
            </div>
            <div v-else-if="headerHasMedia" class="flex-[2]">
              <label class="block text-xs text-n-slate-11 mb-1">{{
                t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_MEDIA_LABEL')
              }}</label>
              <div class="flex items-center gap-2">
                <input
                  ref="headerFileInput"
                  type="file"
                  :accept="headerAcceptAttr"
                  class="hidden"
                  @change="onHeaderFileChange"
                />
                <button
                  type="button"
                  class="h-9 px-3 rounded-md text-xs outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                  @click="() => headerFileInput && headerFileInput.click()"
                >
                  {{
                    headerFileName ||
                    t('INBOX_MGMT.TEMPLATES.CREATE.CHOOSE_FILE')
                  }}
                </button>
                <button
                  v-if="headerMediaFile"
                  type="button"
                  class="h-9 px-3 rounded-md text-xs bg-n-ruby-9 text-white hover:bg-n-ruby-10"
                  @click="clearHeaderFile"
                >
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
                </button>
              </div>
              <div
                v-if="headerMediaFile && headerType === 'DOCUMENT'"
                class="mt-2 text-xs text-n-slate-11"
              >
                {{
                  t('INBOX_MGMT.TEMPLATES.CREATE.DOC_SELECTED', {
                    name: headerFileName,
                  })
                }}
              </div>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="outline outline-1 outline-n-weak rounded-lg p-3">
          <div class="flex items-center justify-between mb-2">
            <label class="text-xs text-n-slate-11">{{
              t('INBOX_MGMT.TEMPLATES.CREATE.BODY')
            }}</label>
            <div class="flex items-center gap-2">
              <span class="text-[11px] text-n-slate-11">{{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLES') }}:
                {{ variablesInBody.length }}</span>
              <button
                class="h-8 px-2 rounded-md text-xs bg-n-brand text-white hover:bg-n-brand-dark"
                type="button"
                @click="insertVariableAtCursor"
              >
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.INSERT_VARIABLE') }}
              </button>
            </div>
          </div>
          <textarea
            v-model="bodyText"
            rows="6"
            class="w-full px-3 py-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
            :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.BODY_PH')"
          />
        </div>

        <!-- Footer -->
        <div class="outline outline-1 outline-n-weak rounded-lg p-3">
          <label class="block text-xs text-n-slate-11 mb-1">{{
            t('INBOX_MGMT.TEMPLATES.CREATE.FOOTER')
          }}</label>
          <input
            v-model="footerText"
            type="text"
            class="w-full h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
            :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.FOOTER_PH')"
          />
        </div>

        <!-- Buttons -->
        <div class="outline outline-1 outline-n-weak rounded-lg p-3">
          <div class="flex items-center justify-between">
            <h4 class="text-xs text-n-slate-11">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.BUTTONS') }}
            </h4>
          </div>

          <!-- Quick replies -->
          <div class="mt-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs text-n-slate-11">{{ t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLIES') }} ({{
                  quickReplies.length
                }}/3)</span>
              <button
                type="button"
                class="h-8 px-2 rounded-md text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-50"
                :disabled="!canAddQuickReply"
                @click="addQuickReply"
              >
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.ADD_QUICK_REPLY') }}
              </button>
            </div>
            <div class="space-y-2">
              <div
                v-for="(qr, i) in quickReplies"
                :key="`qr-${i}`"
                class="flex items-center gap-2"
              >
                <input
                  v-model="quickReplies[i]"
                  type="text"
                  class="flex-1 h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
                  :placeholder="
                    t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLY_LABEL_PH')
                  "
                />
                <button
                  type="button"
                  class="h-8 px-2 rounded-md text-xs bg-n-ruby-9 text-white hover:bg-n-ruby-10"
                  @click="removeQuickReply(i)"
                >
                  {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Call to action -->
          <div class="mt-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs text-n-slate-11">{{ t('INBOX_MGMT.TEMPLATES.CREATE.CTA_BUTTONS') }} ({{
                  ctaButtons.length
                }}/2)</span>
              <button
                type="button"
                class="h-8 px-2 rounded-md text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-50"
                :disabled="!canAddCta"
                @click="addCta"
              >
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.ADD_CTA') }}
              </button>
            </div>
            <div class="space-y-2">
              <div
                v-for="(btn, i) in ctaButtons"
                :key="`cta-${i}`"
                class="grid grid-cols-5 gap-2 items-center"
              >
                <select
                  v-model="ctaButtons[i].type"
                  class="col-span-1 h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12"
                >
                  <option value="URL">URL</option>
                  <option value="PHONE_NUMBER">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.PHONE') }}
                  </option>
                </select>
                <input
                  v-model="ctaButtons[i].label"
                  type="text"
                  class="col-span-2 h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12"
                  :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.CTA_LABEL_PH')"
                />
                <input
                  v-model="ctaButtons[i].value"
                  type="text"
                  class="col-span-2 h-9 px-3 rounded-md outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12"
                  :placeholder="
                    btn.type === 'URL'
                      ? t('INBOX_MGMT.TEMPLATES.CREATE.CTA_URL_PH')
                      : t('INBOX_MGMT.TEMPLATES.CREATE.CTA_PHONE_PH')
                  "
                />
                <div class="col-span-5 flex justify-end">
                  <button
                    type="button"
                    class="h-8 px-2 rounded-md text-xs bg-n-ruby-9 text-white hover:bg-n-ruby-10"
                    @click="removeCta(i)"
                  >
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Variable Samples Section -->
        <div
          v-if="getAllVariables.length > 0"
          class="outline outline-1 outline-n-weak rounded-lg p-3"
        >
          <div class="mb-3">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLE_SAMPLES_TITLE') }}
            </h4>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLE_SAMPLES_DESC') }}
            </p>
          </div>

          <div class="space-y-3">
            <!-- Header Variables -->
            <div v-if="headerType === 'TEXT' && headerText" class="space-y-2">
              <h5 class="text-xs font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER') }}
              </h5>
              <div
                v-for="varNum in getAllVariables.filter(v =>
                  headerText.includes(`{{${v}}}`)
                )"
                :key="`header-${varNum}`"
                class="grid grid-cols-3 gap-2 items-center"
              >
                <div
                  class="text-xs text-n-slate-11 bg-n-slate-3 px-2 py-1 rounded"
                >
                  {{ formatVariable(varNum) }}
                </div>
                <select
                  v-model="variableTypes[varNum]"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                >
                  <option value="text">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_TEXT') }}
                  </option>
                  <option value="number">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_NUMBER') }}
                  </option>
                </select>
                <input
                  v-model="sampleVariables[varNum]"
                  type="text"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                  :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.SAMPLE_VALUE')"
                />
              </div>
            </div>

            <!-- Body Variables -->
            <div v-if="bodyText" class="space-y-2">
              <h5 class="text-xs font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.BODY') }}
              </h5>
              <div
                v-for="varNum in getAllVariables.filter(v =>
                  bodyText.includes(`{{${v}}}`)
                )"
                :key="`body-${varNum}`"
                class="grid grid-cols-3 gap-2 items-center"
              >
                <div
                  class="text-xs text-n-slate-11 bg-n-slate-3 px-2 py-1 rounded"
                >
                  {{ formatVariable(varNum) }}
                </div>
                <select
                  v-model="variableTypes[varNum]"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                >
                  <option value="text">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_TEXT') }}
                  </option>
                  <option value="number">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_NUMBER') }}
                  </option>
                </select>
                <input
                  v-model="sampleVariables[varNum]"
                  type="text"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                  :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.SAMPLE_VALUE')"
                />
              </div>
            </div>

            <!-- Footer Variables -->
            <div v-if="footerText" class="space-y-2">
              <h5 class="text-xs font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.FOOTER') }}
              </h5>
              <div
                v-for="varNum in getAllVariables.filter(v =>
                  footerText.includes(`{{${v}}}`)
                )"
                :key="`footer-${varNum}`"
                class="grid grid-cols-3 gap-2 items-center"
              >
                <div
                  class="text-xs text-n-slate-11 bg-n-slate-3 px-2 py-1 rounded"
                >
                  {{ formatVariable(varNum) }}
                </div>
                <select
                  v-model="variableTypes[varNum]"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                >
                  <option value="text">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_TEXT') }}
                  </option>
                  <option value="number">
                    {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_NUMBER') }}
                  </option>
                </select>
                <input
                  v-model="sampleVariables[varNum]"
                  type="text"
                  class="h-8 px-2 rounded-md outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12"
                  :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.SAMPLE_VALUE')"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <NextButton
            size="sm"
            solid
            blue
            :disabled="isSubmitting"
            :loading="isSubmitting"
            :label="t('INBOX_MGMT.TEMPLATES.CREATE.CREATE_BTN')"
            @click="onCreate"
          />
          <button
            type="button"
            class="ml-2 h-8 px-3 rounded-md text-xs outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-2"
            @click="resetForm"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.RESET') }}
          </button>
        </div>
      </div>

      <!-- Right: 20% Live Preview -->
      <div>
        <div class="outline outline-1 outline-n-weak rounded-lg p-3">
          <h4 class="text-xs text-n-slate-11 mb-2">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW') }}
          </h4>
          <div
            class="rounded-lg p-3 space-y-2 min-h-[200px] relative overflow-hidden"
            style="
              background-image: url('https://waba.huechat.io/uploads/default/wpbox/bg.png');
              background-size: cover;
              background-position: center;
            "
          >
            <!-- Message Bubble with Connected Buttons -->
            <div
              class="bg-white rounded-2xl shadow-lg max-w-[280px] ml-auto relative overflow-hidden"
            >
              <!-- Message Content -->
              <div class="p-4">
                <!-- Header Preview -->
                <div v-if="headerType !== 'NONE'" class="mb-3">
                  <!-- Media Header Preview -->
                  <div
                    v-if="headerHasMedia && headerMediaPreviewUrl"
                    class="mb-2"
                  >
                    <img
                      v-if="headerType === 'IMAGE'"
                      :src="headerMediaPreviewUrl"
                      class="w-full rounded-md object-cover max-h-40"
                      alt="header preview"
                    />
                    <video
                      v-else-if="headerType === 'VIDEO'"
                      :src="headerMediaPreviewUrl"
                      controls
                      class="w-full rounded-md max-h-48"
                    />
                  </div>
                  <!-- Text Header Preview -->
                  <div
                    v-else-if="headerHasText"
                    class="text-sm font-semibold"
                    style="color: #000000 !important"
                  >
                    {{ getPreviewHeader }}
                  </div>
                  <!-- Document Header Preview -->
                  <div
                    v-else-if="headerType === 'DOCUMENT' && headerMediaFile"
                    class="flex items-center gap-2 p-2 bg-gray-100 rounded-md"
                  >
                    <span class="text-lg">📄</span>
                    <span class="text-xs" style="color: #4b5563 !important">{{
                      headerFileName
                    }}</span>
                  </div>
                </div>

                <!-- Body Preview -->
                <div
                  class="text-sm whitespace-pre-line mb-2 leading-relaxed"
                  style="color: #000000 !important"
                >
                  {{ getPreviewBody }}
                </div>

                <!-- Footer Preview -->
                <div
                  v-if="footerText"
                  class="text-[11px] mb-2"
                  style="color: #6b7280 !important"
                >
                  {{ getPreviewFooter }}
                </div>

                <!-- Timestamp -->
                <div
                  class="text-[10px] text-right mt-1"
                  style="color: #9ca3af !important"
                >
                  8:14 AM
                </div>
              </div>

              <!-- Connected Buttons -->
              <div
                v-if="
                  getPreviewQuickReplies.length || getPreviewCtaButtons.length
                "
                class="border-t border-gray-200"
              >
                <!-- Quick Reply Buttons -->
                <div
                  v-for="(qr, i) in getPreviewQuickReplies"
                  :key="`pqr-${i}`"
                  class="px-4 py-3 border-b border-gray-200 flex items-center justify-center gap-3 last:border-b-0"
                  style="color: #0084ff !important"
                >
                  <span class="text-lg">←</span>
                  <span class="text-sm font-medium">{{ qr }}</span>
                </div>

                <!-- CTA Buttons -->
                <div
                  v-for="(btn, i) in getPreviewCtaButtons"
                  :key="`pcta-${i}`"
                  class="px-4 py-3 border-b border-gray-200 flex items-center justify-center gap-3 last:border-b-0"
                  style="color: #0084ff !important"
                >
                  <span v-if="btn.type === 'URL'" class="text-lg">🔗</span>
                  <span v-else-if="btn.type === 'PHONE_NUMBER'"
class="text-lg"
                    >📞</span>
                  <span class="text-sm font-medium">{{ btn.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
